import { BookmarkAdd, ChevronDown, Copy01, FaceSmile, File02, InfoCircle, Save01 } from "@untitledui/icons";
import { createContext, forwardRef, type ReactNode, useContext } from "react";
import type { MessageTemplate } from "@/api/message-templates";
import { Button } from "@/components/base/buttons/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cx } from "@/utils/cx";

export const toolbarButtonClassName =
  "inline-flex cursor-pointer items-center gap-1.5 rounded-md border bg-primary py-2 text-sm leading-5 font-semibold text-secondary transition-colors duration-100 outline-hidden focus-visible:ring-4 focus-visible:ring-brand-100 disabled:cursor-not-allowed disabled:opacity-50";

type ToolbarButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: ReactNode;
  label: string;
  active?: boolean;
  iconOnly?: boolean;
};

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ icon, label, active, iconOnly, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      className={cx(
        toolbarButtonClassName,
        iconOnly ? "px-2.5" : "px-3.5",
        active ? "border-brand-500" : "border-gray-300 hover:bg-primary_hover",
        className,
      )}
      {...props}
    >
      {icon}
      {!iconOnly && label}
    </button>
  ),
);

ToolbarButton.displayName = "ToolbarButton";

export const ComposerPortalContainer = createContext<HTMLElement | null>(null);

export const stopEscapePropagation = (event: React.KeyboardEvent) => {
  if (event.key === "Escape") event.stopPropagation();
};

const menuContentClassName =
  "z-[130] flex flex-col gap-0.5 rounded-xl border border-secondary bg-primary p-1.5 shadow-lg";

const menuItemClassName =
  "w-full cursor-pointer rounded-md px-2.5 py-2 text-left transition-colors duration-100 focus:bg-primary_hover data-[highlighted]:bg-primary_hover";

type ComposerMenuProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  width: number;
  align?: "start" | "end";
  scroll?: boolean;
  onCloseAutoFocus?: (event: Event) => void;
  children: ReactNode;
};

function ComposerMenu({
  open,
  onOpenChange,
  trigger,
  width,
  align = "start",
  scroll = true,
  onCloseAutoFocus,
  children,
}: ComposerMenuProps) {
  const portalContainer = useContext(ComposerPortalContainer);
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align={align}
        sideOffset={8}
        style={{ width }}
        onCloseAutoFocus={onCloseAutoFocus}
        portalContainer={portalContainer}
        onKeyDown={stopEscapePropagation}
        className={cx(menuContentClassName, scroll && "max-h-[260px] overflow-y-auto")}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type MenuState = { open: boolean; onOpenChange: (open: boolean) => void };

export function VariableMenu({
  tokens,
  unavailable,
  onInsert,
  onCloseAutoFocus,
  disabled,
  ...menu
}: MenuState & {
  tokens: string[];
  unavailable?: Record<string, string>;
  onInsert: (token: string) => void;
  onCloseAutoFocus: (event: Event) => void;
  disabled?: boolean;
}) {
  return (
    <ComposerMenu
      {...menu}
      width={280}
      scroll={false}
      onCloseAutoFocus={onCloseAutoFocus}
      trigger={
        <ToolbarButton
          disabled={disabled}
          active={menu.open}
          label="Variable"
          icon={<span className="font-mono text-[13px] leading-none">{"{ }"}</span>}
        />
      }
    >
      {tokens.map((token) => {
        const unavailableReason = unavailable?.[token];
        return (
          <DropdownMenuItem
            key={token}
            onSelect={() => onInsert(token)}
            className={cx(
              menuItemClassName,
              "flex flex-col items-stretch gap-1",
              unavailableReason && "bg-utility-warning-50 data-[highlighted]:bg-utility-warning-100",
            )}
          >
            <span className="flex items-center gap-2 font-mono text-[13px] text-tertiary">
              {token}
              {unavailableReason && <InfoCircle aria-hidden className="size-4 shrink-0 text-utility-warning-600" />}
            </span>
            {unavailableReason && <span className="text-xs leading-[18px] text-tertiary">{unavailableReason}</span>}
          </DropdownMenuItem>
        );
      })}
    </ComposerMenu>
  );
}

type ComposerTemplateOption = Pick<MessageTemplate, "id" | "name" | "content">;

export function TemplatesMenu({
  templates,
  loadedTemplateId,
  onPick,
  disabled,
  ...menu
}: MenuState & {
  templates: ComposerTemplateOption[];
  loadedTemplateId: string | null;
  onPick: (templateId: string) => void;
  disabled?: boolean;
}) {
  return (
    <ComposerMenu
      {...menu}
      width={320}
      trigger={
        <ToolbarButton
          disabled={disabled}
          active={menu.open}
          label="Templates"
          icon={<File02 aria-hidden className="size-4" />}
        />
      }
    >
      {templates.length === 0 ? (
        <p className="px-2.5 py-2 text-sm text-tertiary">No saved templates yet.</p>
      ) : (
        templates.map((template) => (
          <DropdownMenuItem
            key={template.id}
            onSelect={() => onPick(template.id)}
            className={cx(menuItemClassName, "flex flex-col items-stretch gap-0.5")}
          >
            <span className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">{template.name}</span>
              {loadedTemplateId === template.id && (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand-950">
                  <span aria-hidden className="size-1.5 rounded-full bg-brand-950" />
                  In use
                </span>
              )}
            </span>
            <span className="line-clamp-2 text-[13px] leading-[18px] text-tertiary">{template.content}</span>
          </DropdownMenuItem>
        ))
      )}
    </ComposerMenu>
  );
}

const EMOJIS = ["🤍", "🙌", "🎉", "🔥", "💛", "✨", "👏", "😊", "💬", "📦", "🎯", "👀"];

export function EmojiMenu({
  onInsert,
  onCloseAutoFocus,
  disabled,
  ...menu
}: MenuState & { onInsert: (emoji: string) => void; onCloseAutoFocus: (event: Event) => void; disabled?: boolean }) {
  return (
    <ComposerMenu
      {...menu}
      width={232}
      scroll={false}
      onCloseAutoFocus={onCloseAutoFocus}
      trigger={
        <ToolbarButton
          disabled={disabled}
          active={menu.open}
          iconOnly
          label="Emoji"
          icon={<FaceSmile aria-hidden className="size-4" />}
        />
      }
    >
      <div className="grid grid-cols-6 gap-0.5">
        {EMOJIS.map((emoji) => (
          <DropdownMenuItem
            key={emoji}
            onSelect={() => onInsert(emoji)}
            className="cursor-pointer justify-center rounded-md p-1.5 text-lg transition-colors duration-100 data-[highlighted]:bg-primary_hover"
          >
            {emoji}
          </DropdownMenuItem>
        ))}
      </div>
    </ComposerMenu>
  );
}

export function TemplateActions({
  loadedTemplateName,
  canSave,
  canUpdate,
  menuOpen,
  onMenuOpenChange,
  onSaveAsNew,
  onUpdate,
}: {
  loadedTemplateName: string | null;
  canSave: boolean;
  canUpdate: boolean;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onSaveAsNew: () => void;
  onUpdate: () => void;
}) {
  if (!loadedTemplateName) {
    return (
      <Button color="tertiary" size="sm" iconLeading={BookmarkAdd} isDisabled={!canSave} onClick={onSaveAsNew}>
        Save as template
      </Button>
    );
  }

  return (
    <ComposerMenu
      open={menuOpen}
      onOpenChange={onMenuOpenChange}
      width={260}
      align="end"
      scroll={false}
      trigger={
        <button
          type="button"
          disabled={!canSave}
          className={cx(toolbarButtonClassName, "border-gray-300 px-3.5 hover:bg-primary_hover")}
        >
          Update template
          <ChevronDown
            aria-hidden
            className={cx("size-4 text-fg-quaternary transition-transform duration-150", menuOpen && "rotate-180")}
          />
        </button>
      }
    >
      <DropdownMenuItem
        disabled={!canUpdate}
        onSelect={onUpdate}
        className={cx(menuItemClassName, "flex items-center gap-2.5 text-sm text-secondary")}
      >
        <Save01 aria-hidden className="size-4 shrink-0 text-fg-quaternary" />
        <span className="truncate">Update “{loadedTemplateName}”</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={onSaveAsNew}
        className={cx(menuItemClassName, "flex items-center gap-2.5 text-sm text-secondary")}
      >
        <Copy01 aria-hidden className="size-4 shrink-0 text-fg-quaternary" />
        Save as new template
      </DropdownMenuItem>
    </ComposerMenu>
  );
}
